import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { News } from "../types/News";
import ReactMarkdown from "react-markdown";
import { supabase } from "../lib/supabase";

const NewsDetailPage: React.FC = () => {
  const { newsId } = useParams();
  const [news, setNews] = useState<News | null>(null);
  const [loading, setLoading] = useState(true);
  window.scrollTo({ top: 0, behavior: "smooth" });

  useEffect(() => {
    const fetchNews = async () => {
        const { data, error } = await supabase.from('news').select('*').eq('id', newsId).single();
        if (error) {
            console.error("Haber detay alınamadı:", error);
        } else {
            setNews(data as News);
        }
        setLoading(false);
    };
    fetchNews();
  }, [newsId]);

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!news) {
    return (
      <div className="flex justify-center items-center min-h-screen text-gray-500 text-xl">
        Haber bulunamadı.
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-6 py-20 font-montserrat text-gray-800">
      {/* Başlık ve Tarih */}
      <div className="mb-10 text-center">
        <h1 className="text-4xl md:text-5xl font-bold mb-2">{news.Title}</h1>
        <p className="text-gray-500 text-sm">{news.Date} – Ayvalık Marin</p>
      </div>

      {/* Kapak Görseli */}
      <div className="rounded-xl overflow-hidden shadow-lg mb-14">
        <img
          src={news.ContainerImagePath}
          alt={news.Title}
          className="w-full h-[400px] object-cover"
        />
      </div>

      {/* İçerik + Yan Panel */}
      <div className="flex flex-col lg:flex-row gap-10">
        {/* Haber İçeriği */}
        <div className="flex-1">
          <div className="prose prose-lg max-w-none text-justify leading-relaxed whitespace-pre-line">
            <ReactMarkdown>{news.FullText}</ReactMarkdown>
          </div>
        </div>

        {/* Özet Paneli */}
        <div className="lg:w-1/3 space-y-6">
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 shadow-md sticky top-28">
            <h2 className="text-xl font-semibold mb-2">Kısa Bilgi</h2>
            <p className="text-gray-600 text-sm">{news.ShortText}</p>
          </div>
        </div>
      </div>

      {/* Video + Galeri */}
      {(news.VideoPath || (news.Images && news.Images.length > 0)) && (
        <div className="mt-20 grid grid-cols-1 lg:grid-cols-2 gap-10">
          {/* Video */}
          {news.VideoPath && (
            <div>
              <h2 className="text-xl font-semibold mb-3">Haber Videosu</h2>
              <p className="text-sm text-gray-500 mb-4">
                Habere ait görüntüleri izleyebilirsiniz.
              </p>
              <div className="rounded-lg shadow-xl overflow-hidden">
                <video
                  src={news.VideoPath}
                  controls
                  className="w-full h-80 object-cover rounded-lg"
                />
              </div>
            </div>
          )}

          {/* Galeri */}
          {news.Images && news.Images.length > 0 && (
            <div>
              <h2 className="text-xl font-semibold mb-3">Fotoğraf Galerisi</h2>
              <p className="text-sm text-gray-500 mb-4">
                Habere ait bazı kareleri aşağıda görebilirsiniz.
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                {news.Images.map((img, i) => (
                  <div
                    key={i}
                    className="w-full h-40 sm:h-48 rounded-lg overflow-hidden shadow-md hover:scale-105 transition-transform duration-300"
                  >
                    <img
                      src={img}
                      alt={`Galeri ${i}`}
                      className="w-full h-full object-cover"
                    />
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default NewsDetailPage;
